import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { NavBar } from '../../components/NavBar'
import { getBlogPostBySlug, type IBlogPostWithAuthor } from '../../api/blog'

export const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [post, setPost] = useState<IBlogPostWithAuthor | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    window.scrollTo(0, 0)
    if (slug) {
      loadPost(slug)
    }
  }, [slug])

  const loadPost = async (postSlug: string) => {
    try {
      setIsLoading(true)
      const blogPost = await getBlogPostBySlug(postSlug)
      setPost(blogPost)
    } catch (error) {
      console.error('Error loading blog post:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <NavBar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-xl text-gray-500">Loading...</div>
        </div>
      </div>
    )
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <NavBar />
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h1 className="text-4xl font-bold mb-4">404</h1>
          <p className="text-xl text-gray-600 mb-6">Blog post not found</p>
          <button
            onClick={() => navigate('/blog')}
            className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Back to Blog
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <NavBar />

      <article className="max-w-3xl mx-auto px-4 py-8 mt-20">
        {/* Cover Image */}
        {post.coverImageUrl && (
          <div className="mb-8 -mx-4 md:mx-0">
            <img
              src={post.coverImageUrl}
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover md:rounded-2xl"
            />
          </div>
        )}

        {/* Header */}
        <header className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            {post.title}
          </h1>

          {/* Author and Date */}
          <div className="flex items-center gap-4 text-gray-600">
            <div className="flex items-center gap-2">
              {post.authorAvatar ? (
                <img
                  src={post.authorAvatar}
                  alt={post.authorUsername}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-lg font-semibold text-gray-600">
                    {post.authorUsername.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <span className="font-medium">{post.authorUsername}</span>
            </div>
            <span className="text-gray-400">•</span>
            <time dateTime={post.created}>{formatDate(post.created)}</time>
            {post.updated !== post.created && (
              <>
                <span className="text-gray-400">•</span>
                <span className="text-sm text-gray-500">
                  Updated {formatDate(post.updated)}
                </span>
              </>
            )}
          </div>
        </header>

        {/* Content */}
        <div
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: post.contentHtml }}
        />

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-gray-200">
          <button
            onClick={() => navigate('/blog')}
            className="text-gray-600 hover:text-black transition-colors flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to all posts
          </button>
        </footer>
      </article>
    </div>
  )
}
